import { useEffect, useCallback, useRef, useState } from 'react';
import type { Stroke, PubSubStrokeCommit, ChannelConfig } from '@twitch-draw/shared';
import { useTwitch } from '../twitch/useTwitch';
import { ebsClient } from '../lib/api';
import { DrawCanvas } from '../components/DrawCanvas';
import { Toolbar } from '../components/Toolbar';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { playSound, preloadSound } from '../hooks/useSound';
import { useDrawingState } from '../hooks/useDrawingState';

const DEFAULT_DISPLAY_DURATION_MS = 30000;

const BITS_SKUS = [
  { sku: 'draw-1', bits: 100 },
  { sku: 'draw-2', bits: 300 },
  { sku: 'draw-3', bits: 500 },
  { sku: 'draw-4', bits: 1000 },
  { sku: 'draw-5', bits: 2000 },
  { sku: 'draw-6', bits: 5000 },
];

function getSkuForCost(cost: number): { sku: string; bits: number } {
  for (const item of BITS_SKUS) {
    if (item.bits >= cost) {
      return item;
    }
  }
  return BITS_SKUS[BITS_SKUS.length - 1];
}

export default function App() {
  const twitch = useTwitch();
  const [channelConfig, setChannelConfig] = useState<ChannelConfig | null>(null);

  const drawing = useDrawingState({
    channelId: twitch.auth?.channelId,
    userId: twitch.auth?.userId,
    channelConfig,
  });

  const displayDurationMs = channelConfig?.displayDurationMs ?? DEFAULT_DISPLAY_DURATION_MS;

  useEffect(() => {
    if (!twitch.isReady || !twitch.auth) return;

    ebsClient.setToken(twitch.auth.token);
    drawing.setStrokes([]);
    drawing.setAppState('drawing');

    ebsClient.getChannelConfig()
      .then(res => setChannelConfig(res.config))
      .catch(err => console.error('Failed to load channel config:', err));
  }, [twitch.isReady, twitch.auth]);

  useEffect(() => {
    preloadSound('./sounds/draw.mp3');
  }, []);

  const expirationTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const scheduleStrokeExpiration = useCallback((strokeId: string) => {
    const existingTimer = expirationTimersRef.current.get(strokeId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      drawing.setStrokes(prev => prev.filter(s => s.id !== strokeId));
      expirationTimersRef.current.delete(strokeId);
    }, displayDurationMs);

    expirationTimersRef.current.set(strokeId, timer);
  }, [drawing, displayDurationMs]);

  useEffect(() => {
    return () => {
      expirationTimersRef.current.forEach(timer => clearTimeout(timer));
      expirationTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    twitch.onPubSubMessage((message) => {
      if (message.t === 'stroke.commit') {
        const strokeMsg = message as PubSubStrokeCommit;
        const newStroke: Stroke = {
          id: strokeMsg.id,
          channelId: strokeMsg.c,
          userId: strokeMsg.u,
          createdAt: strokeMsg.ts,
          color: strokeMsg.col,
          width: strokeMsg.w,
          durationMs: strokeMsg.ms,
          points: strokeMsg.p,
        };
        drawing.setStrokes(s => [...s, newStroke]);
        playSound('./sounds/draw.mp3', 0.4);

        scheduleStrokeExpiration(newStroke.id);
      } else if (message.t === 'canvas.clear') {
        expirationTimersRef.current.forEach(timer => clearTimeout(timer));
        expirationTimersRef.current.clear();
        drawing.setStrokes([]);
      }
    });
  }, [twitch, scheduleStrokeExpiration]);

  useEffect(() => {
    twitch.onTransaction(async (tx) => {
      const strokesToSend = drawing.pendingStrokesRef.current;
      if (strokesToSend.length === 0) return;

      try {
        await ebsClient.commitStrokesBatch(
          strokesToSend,
          tx.transactionId,
          tx.transactionReceipt,
          tx.displayName
        );
        drawing.setPendingStrokes([]);
      } catch (err) {
        console.error('Failed to commit strokes:', err);
      }

      drawing.setTotalCost(0);
      drawing.setAppState('drawing');
    });

    twitch.onTransactionCancelled(() => {
      drawing.setTotalCost(0);
      drawing.setAppState('drawing');
    });
  }, [twitch]);

  const handleConfirmPayment = useCallback(() => {
    const { sku } = getSkuForCost(drawing.totalCost);
    twitch.useBits(sku);
    drawing.setAppState('processing');
  }, [drawing.totalCost, twitch]);

  const handleClearCanvas = useCallback(async () => {
    try {
      await ebsClient.clearCanvas();
    } catch (err) {
      console.error('Failed to clear canvas:', err);
    }
  }, []);

  if (twitch.error) {
    return <div className="error">{twitch.error}</div>;
  }

  if (drawing.appState === 'loading') {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div
      className="overlay-container"
      onMouseEnter={drawing.handleMouseEnter}
      onMouseLeave={drawing.handleMouseLeave}
    >
      <DrawCanvas
        strokes={drawing.allStrokes}
        currentStroke={drawing.currentStroke}
        onStartStroke={drawing.handleStartStroke}
        onContinueStroke={drawing.handleContinueStroke}
        onEndStroke={drawing.handleEndStroke}
        disabled={drawing.appState !== 'drawing'}
        isEraser={drawing.isEraser}
        color={drawing.color}
      />

      {drawing.showToolbar && (
        <Toolbar
          color={drawing.color}
          width={drawing.width}
          onColorChange={drawing.setColor}
          onWidthChange={drawing.setWidth}
          disabled={drawing.appState !== 'drawing'}
          isEraser={drawing.isEraser}
          onEraserToggle={drawing.handleEraserToggle}
          onEraserOff={drawing.handleEraserOff}
          showClearButton={twitch.isModerator}
          onClearCanvas={handleClearCanvas}
        />
      )}

      {drawing.showToolbar && drawing.pendingStrokes.length > 0 && drawing.appState === 'drawing' && (
        <div style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 8,
          background: 'rgba(0,0,0,0.8)',
          padding: '12px 16px',
          borderRadius: 8,
          alignItems: 'center',
        }}>
          <span style={{ color: 'white', fontSize: 14 }}>
            {drawing.pendingCost} Bits
          </span>
          <button
            onClick={drawing.handleClearPending}
            style={{
              padding: '6px 12px',
              border: 'none',
              borderRadius: 4,
              background: '#555',
              color: 'white',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
          <button
            onClick={drawing.handleSubmit}
            style={{
              padding: '6px 16px',
              border: 'none',
              borderRadius: 4,
              background: '#9147ff',
              color: 'white',
              fontSize: 14,
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            Send
          </button>
        </div>
      )}

      {drawing.appState === 'confirming' && (
        <ConfirmDialog
          bits={drawing.totalCost}
          onConfirm={handleConfirmPayment}
          onCancel={drawing.handleCancelPayment}
        />
      )}
    </div>
  );
}
